// Lotes de despacho (PO1-2377, épico PO1-2374): regras puras da tela — abas,
// rótulos de status/origem, o que cada status permite, validação dos gatilhos.
// Sem React, para testar isolado.
import moment from "moment";

export const PERMISSAO_VER = "hub.operacao.despacho.visualizar";
export const PERMISSAO_EDITAR = "hub.operacao.despacho.editar";

export const ABA_COM_ERRO = "com-erro";
export const ABAS = [
  { chave: "aberto", rotulo: "Abertos" },
  { chave: "fechado", rotulo: "Fechados" },
  { chave: "despachado", rotulo: "Despachados" },
  { chave: ABA_COM_ERRO, rotulo: "Com erro" },
];

// Link direto (PO1-3493): `?lote=<id>` abre o detalhe daquele lote ao montar e
// `?aba=<chave>` escolhe a aba. Valor inválido é ignorado (tela abre normal).
export function linkDaUrl(search) {
  const q = new URLSearchParams(search || "");
  const lote = Number(q.get("lote"));
  const aba = q.get("aba");
  return {
    lote: Number.isInteger(lote) && lote > 0 ? lote : null,
    aba: ABAS.some((a) => a.chave === aba) ? aba : null,
  };
}

const STATUS_LOTE = {
  aberto: { rotulo: "Aberto", tom: "info" },
  fechado: { rotulo: "Fechado", tom: "warning" },
  despachado: { rotulo: "Despachado", tom: "success" },
};
export const rotuloStatusLote = (s) => (STATUS_LOTE[s] ? STATUS_LOTE[s].rotulo : s || "-");
export const tomStatusLote = (s) => (STATUS_LOTE[s] ? STATUS_LOTE[s].tom : "info");

const ORIGEM_LOTE = { manual: "Manual", volume: "Gatilho por volume", horario: "Gatilho por horário" };
export const rotuloOrigem = (o) => ORIGEM_LOTE[o] || o || "-";

// Status do envio (NotaFiscalEnvio da PO1-3378).
const STATUS_ENVIO = {
  criando: { rotulo: "Criando", tom: "info" },
  pendente: { rotulo: "Pendente", tom: "info" },
  emitida: { rotulo: "Emitida", tom: "success" },
  falha: { rotulo: "Falha", tom: "warning" },
  cancelada: { rotulo: "Cancelada", tom: "info" },
};
export const rotuloStatusEnvio = (s) => (STATUS_ENVIO[s] ? STATUS_ENVIO[s].rotulo : s || "-");
export const tomStatusEnvio = (s) => (STATUS_ENVIO[s] ? STATUS_ENVIO[s].tom : "info");

// O que o status do lote permite (o backend confirma com 409).
export const podeFechar = (lote) => Boolean(lote && lote.status === "aberto");
export const podeDespachar = (lote) => Boolean(lote && lote.status === "fechado");
export const podeMexerEnvios = (lote) => Boolean(lote && (lote.status === "aberto" || lote.status === "fechado"));
// Pedir (ou refazer) a coleta: só lote já despachado, com ou sem número de coleta.
export const podeSolicitarColeta = (lote) => Boolean(lote && lote.status === "despachado");

// Reenviar só vale para envios com falha.
export const apenasFalhas = (envios) => (envios || []).filter((e) => e && e.status === "falha");

export const hojeIso = () => moment().format("YYYY-MM-DD");

export const nomeTransportadora = (t) => (t && (t.fantasia || t.razaoSocial || t.nome || (t.id ? `#${t.id}` : ""))) || "";

// ─── Resultados das ações ────────────────────────────────────────────────────
export function textoDesvinculo(r) {
  const desvinculados = Number(r && r.desvinculados) || 0;
  const cancelados = Number(r && r.cancelados) || 0;
  const falhas = Array.isArray(r && r.falhasCancelamento) ? r.falhasCancelamento : [];
  let texto = `${desvinculados} envio(s) desvinculado(s)`;
  if (cancelados) texto += `, ${cancelados} cancelado(s) na transportadora`;
  if (falhas.length) texto += `; ${falhas.length} não pôde(puderam) ser cancelado(s): ${falhas.map((f) => `#${f.idEnvio} ${f.erro || ""}`.trim()).join(" · ")}`;
  return `${texto}.`;
}

export function textoReenvio(r) {
  const resultados = Array.isArray(r && r.resultados) ? r.resultados : [];
  const ok = resultados.filter((x) => x && x.ok).length;
  const falhas = resultados.filter((x) => x && !x.ok);
  let texto = `${ok} envio(s) reenviado(s)`;
  if (falhas.length) texto += `; ${falhas.length} continua(m) com falha: ${falhas.map((f) => `#${f.idEnvio} ${f.erro || ""}`.trim()).join(" · ")}`;
  return `${texto}.`;
}

// "; 2 nota(s) com falha: #101 CEP inválido · #102 ..." (coleta.falhas) ou "".
const sufixoFalhasColeta = (coleta) => {
  const falhas = Array.isArray(coleta && coleta.falhas) ? coleta.falhas : [];
  if (!falhas.length) return "";
  return `; ${falhas.length} nota(s) com falha: ${falhas.map((f) => `#${f.idNotaFiscal} ${f.erro || ""}`.trim()).join(" · ")}`;
};

export function textoDespacho(lote, coleta) {
  const codigo = (lote && lote.codigo) || "";
  if (!coleta) return { tom: "ok", texto: `Lote ${codigo} despachado.` };
  if (coleta.erro) return { tom: "aviso", texto: `Lote ${codigo} despachado, mas a coleta não foi solicitada: ${coleta.erro}` };
  const falhas = sufixoFalhasColeta(coleta);
  return { tom: falhas ? "aviso" : "ok", texto: `Lote ${codigo} despachado. Coleta nº ${coleta.numeroColeta || "-"} solicitada${falhas}.` };
}

// Resultado do POST /despacho/lotes/:id/coleta (lote já despachado).
export function textoColeta(lote, coleta) {
  const codigo = (lote && lote.codigo) || "";
  if (!coleta || coleta.erro) return { tom: "erro", texto: `A coleta do lote ${codigo} não foi solicitada: ${(coleta && coleta.erro) || "a transportadora não respondeu."}` };
  const falhas = sufixoFalhasColeta(coleta);
  return { tom: falhas ? "aviso" : "ok", texto: `Coleta nº ${coleta.numeroColeta || "-"} solicitada para o lote ${codigo}${falhas}.` };
}

// ─── Gatilhos ────────────────────────────────────────────────────────────────
// Contrato (QA 12/10): o GET devolve TODAS as linhas do embarcador, inclusive as
// inativas; o PUT recebe o ESTADO COMPLETO desejado (pode ser []) e desativa o
// que não vier. A tela mostra uma linha por transportadora com dois campos
// independentes, horário e volume: um gatilho ativo de cada tipo cabe na mesma
// linha; os dois vazios = "Sem gatilho" = a transportadora não vai no payload.
export const HORARIO_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

// Linha vazia (sem gatilho) de uma transportadora.
const linhaVazia = (idTransportadora, nome) => ({ idTransportadora, transportadora: nome, horario: "", volume: "" });

// `ativo` ausente conta como ativo (backend antigo só devolvia os ativos).
export const gatilhoAtivo = (g) => Boolean(g) && (g.ativo === undefined ? true : Boolean(g.ativo));

// Uma linha por transportadora do embarcador, montada só com os gatilhos
// ATIVOS. Transportadora com gatilho ativo que não está na lista de vínculos
// também entra: senão salvar a lista completa a desativaria sem ninguém ver.
export function mesclarGatilhos(transportadoras, gatilhos) {
  const linhas = new Map();
  (transportadoras || []).forEach((t) => linhas.set(String(t.id), linhaVazia(t.id, nomeTransportadora(t))));
  (gatilhos || []).filter(gatilhoAtivo).forEach((g) => {
    const chave = String(g.idTransportadora);
    if (!linhas.has(chave)) linhas.set(chave, linhaVazia(g.idTransportadora, g.transportadora || `#${g.idTransportadora}`));
    const linha = linhas.get(chave);
    if (g.tipo === "horario" && g.horario && !linha.horario) linha.horario = String(g.horario).slice(0, 5);
    if (g.tipo === "volume" && g.volume !== null && g.volume !== undefined && !linha.volume) linha.volume = String(g.volume);
  });
  return Array.from(linhas.values());
}

// Erros por idTransportadora; vazio quando tudo certo. Campo vazio = sem gatilho daquele tipo.
export function validarGatilhos(linhas) {
  const erros = {};
  (linhas || []).forEach((l) => {
    const msgs = [];
    if (l.horario && !HORARIO_RE.test(String(l.horario))) msgs.push("Informe o horário no formato HH:mm.");
    if (l.volume !== "" && l.volume !== null && l.volume !== undefined) {
      const n = Number(l.volume);
      if (!Number.isInteger(n) || n < 1) msgs.push("Informe a quantidade de envios (inteiro a partir de 1).");
    }
    if (msgs.length) erros[l.idTransportadora] = msgs.join(" ");
  });
  return erros;
}

// Payload do PUT: o estado completo. Um item por campo preenchido (horário e/ou
// volume); linha com os dois vazios não entra. Pode devolver [].
export function gatilhosParaSalvar(linhas) {
  const itens = [];
  (linhas || []).forEach((l) => {
    if (l.horario) itens.push({ idTransportadora: l.idTransportadora, tipo: "horario", horario: l.horario, volume: null, ativo: true });
    if (l.volume !== "" && l.volume !== null && l.volume !== undefined) {
      itens.push({ idTransportadora: l.idTransportadora, tipo: "volume", horario: null, volume: Number(l.volume), ativo: true });
    }
  });
  return itens;
}

// Linha da tela ("Fecha às 17:30 · Fecha com 50 envio(s)") ou gatilho do backend.
export function descreverGatilho(g) {
  if (!g) return "Sem gatilho";
  const partes = [];
  if (g.tipo === "horario" || (!g.tipo && g.horario)) partes.push(`Fecha às ${g.horario}`);
  if (g.tipo === "volume" || (!g.tipo && g.volume !== "" && g.volume !== null && g.volume !== undefined)) partes.push(`Fecha com ${g.volume} envio(s)`);
  if (!partes.length) return "Sem gatilho";
  const base = partes.join(" · ");
  return g.tipo && g.ativo === false ? `${base} (inativo)` : base;
}
